import { useCallback, useMemo, useTransition } from "react";

import { SteppedMultiColumnDropdown, V2SelectField } from "@stacklet/ui";
import { graphql, useFragment, usePaginationFragment } from "react-relay";
import { useDebouncedCallback } from "use-debounce";

import { createPathAwareDropdown, column } from "./DefinitionBuilder";

import type { JiraAssigneeValue } from "../schema";
import type { JiraAssignee_accountTagKeys$key } from "./__generated__/JiraAssignee_accountTagKeys.graphql";
import type { JiraAssignee_accountTagValues$key } from "./__generated__/JiraAssignee_accountTagValues.graphql";
import type { JiraAssignee_resourceTagKeys$key } from "./__generated__/JiraAssignee_resourceTagKeys.graphql";
import type { JiraAssignee_whoami$key } from "./__generated__/JiraAssignee_whoami.graphql";

/**
 * Display labels for assignee types
 */
const ASSIGNEE_TYPE_LABELS = {
  EMAIL: "Me",
  RESOURCE_OWNER: "Resource Owner",
  ACCOUNT_OWNER: "Account Owner",
  RESOURCE_TAG: "Resource Tag",
  ACCOUNT_TAG: "Account Tag",
  ACCOUNT_VARIABLE: "Account Variable",
};

const ASSIGNEE_TYPE_TO_ID: Record<string, string> = {
  EMAIL: "me",
  RESOURCE_OWNER: "resource-owner",
  ACCOUNT_OWNER: "account-owner",
  RESOURCE_TAG: "resource-tag",
  ACCOUNT_TAG: "account-tag",
  ACCOUNT_VARIABLE: "account-variable",
};

// Types that have no secondary selection column in the dropdown
const SINGLE_COLUMN_TYPES = new Set([
  "EMAIL",
  "RESOURCE_OWNER",
  "ACCOUNT_OWNER",
]);

/**
 * Converts a JiraAssigneeValue back to dropdown selections array
 */
export function convertAssigneeValueToSelections(
  assigneeValue: JiraAssigneeValue | null | undefined,
): string[] {
  if (!assigneeValue) return ["none"];
  const id = ASSIGNEE_TYPE_TO_ID[assigneeValue.type] ?? "none";

  if (assigneeValue.type === "ACCOUNT_TAG" && assigneeValue.value) {
    const colonIndex = assigneeValue.value.indexOf(":");
    if (colonIndex !== -1) {
      const tagKey = assigneeValue.value.slice(0, colonIndex);
      const tagValue = assigneeValue.value.slice(colonIndex + 1);
      return [id, tagKey, tagValue];
    }
    return [id, assigneeValue.value];
  }

  return !SINGLE_COLUMN_TYPES.has(assigneeValue.type) && assigneeValue.value
    ? [id, assigneeValue.value]
    : [id];
}

/**
 * Converts assignee dropdown selections to structured format
 */
export function convertAssigneeSelections(
  selections: string[],
  whoamiEmail?: string | null,
): JiraAssigneeValue | null {
  const [assigneeType, value] = selections;

  switch (assigneeType) {
    case "none":
      return null;
    case "me":
      return whoamiEmail ? { type: "EMAIL", value: whoamiEmail } : null;
    case "resource-owner":
      return { type: "RESOURCE_OWNER", value: null };
    case "account-owner":
      return { type: "ACCOUNT_OWNER", value: null };
    case "resource-tag":
      return value ? { type: "RESOURCE_TAG", value } : null;
    case "account-tag": {
      const tagKey = value;
      const tagValue = selections[2];
      return tagKey && tagValue
        ? { type: "ACCOUNT_TAG", value: `${tagKey}:${tagValue}` }
        : null;
    }
    case "account-variable":
      return value ? { type: "ACCOUNT_VARIABLE", value } : null;
    default:
      return null;
  }
}

/**
 * Converts JiraAssigneeValue back to display string
 */
export function convertAssigneeValueToDisplayString(
  assigneeValue: JiraAssigneeValue | null | undefined,
  whoamiEmail?: string | null,
): string {
  if (!assigneeValue) {
    return "Unassigned";
  }

  const baseLabel = ASSIGNEE_TYPE_LABELS[assigneeValue.type];

  // Special handling for EMAIL type
  if (assigneeValue.type === "EMAIL") {
    return assigneeValue.value || whoamiEmail || baseLabel;
  }

  // For types with values, append the value
  if (assigneeValue.value) {
    return `${baseLabel} - ${assigneeValue.value}`;
  }

  return baseLabel;
}

interface Props {
  whoamiRef: JiraAssignee_whoami$key;
  resourceTagKeysRef: JiraAssignee_resourceTagKeys$key;
  accountTagKeysRef: JiraAssignee_accountTagKeys$key;
  accountTagValuesRef: JiraAssignee_accountTagValues$key;
  /** Currently selected assignee value (if any) */
  selectedAssignee?: JiraAssigneeValue | null;
  /** Callback when selection changes, receives structured format */
  onChange?: (value: JiraAssigneeValue | null) => void;
}

export function JiraAssignee({
  whoamiRef,
  resourceTagKeysRef,
  accountTagKeysRef,
  accountTagValuesRef,
  selectedAssignee,
  onChange,
}: Props) {
  const [, startTransition] = useTransition();

  const data = useFragment(
    graphql`
      fragment JiraAssignee_whoami on Query {
        whoami {
          email
        }
      }
    `,
    whoamiRef,
  );

  const {
    data: tagKeysData,
    loadNext: loadNextTagKeys,
    hasNext: hasNextTagKeys,
    refetch: refetchTagKeys,
  } = usePaginationFragment(
    graphql`
      fragment JiraAssignee_resourceTagKeys on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "JiraAssigneeResourceTagKeysPaginationQuery") {
        jiraAssigneeResourceTagKeys: resourceTagKeys(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "JiraAssignee__jiraAssigneeResourceTagKeys") {
          edges {
            node {
              key
            }
          }
        }
      }
    `,
    resourceTagKeysRef,
  );

  const {
    data: accountTagKeysData,
    loadNext: loadNextAccountTagKeys,
    hasNext: hasNextAccountTagKeys,
    refetch: refetchAccountTagKeys,
  } = usePaginationFragment(
    graphql`
      fragment JiraAssignee_accountTagKeys on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "JiraAssigneeAccountTagKeysPaginationQuery") {
        jiraAssigneeAccountTagKeys: accountTagKeys(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "JiraAssignee__jiraAssigneeAccountTagKeys") {
          edges {
            node {
              key
            }
          }
        }
      }
    `,
    accountTagKeysRef,
  );

  const {
    data: accountTagValuesData,
    loadNext: loadNextAccountTagValues,
    hasNext: hasNextAccountTagValues,
    refetch: refetchAccountTagValues,
  } = usePaginationFragment(
    graphql`
      fragment JiraAssignee_accountTagValues on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "JiraAssigneeAccountTagValuesPaginationQuery") {
        jiraAssigneeAccountTagValues: accountTagValues(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "JiraAssignee__jiraAssigneeAccountTagValues") {
          edges {
            node {
              value
            }
          }
        }
      }
    `,
    accountTagValuesRef,
  );

  // Transform tag keys data for the dropdown
  const tagKeyItems = useMemo(
    () =>
      tagKeysData.jiraAssigneeResourceTagKeys?.edges.map((edge) => ({
        id: edge.node.key,
        name: edge.node.key,
      })) ?? [],
    [tagKeysData.jiraAssigneeResourceTagKeys?.edges],
  );

  const accountTagKeyItems = useMemo(
    () =>
      accountTagKeysData.jiraAssigneeAccountTagKeys?.edges?.map((edge) => ({
        id: edge.node.key,
        name: edge.node.key,
      })) ?? [],
    [accountTagKeysData.jiraAssigneeAccountTagKeys?.edges],
  );

  const accountTagValueItems = useMemo(
    () =>
      accountTagValuesData.jiraAssigneeAccountTagValues?.edges?.map((edge) => ({
        id: edge.node.value,
        name: edge.node.value,
      })) ?? [],
    [accountTagValuesData.jiraAssigneeAccountTagValues?.edges],
  );

  // Configuration
  const config = useMemo(
    () =>
      createPathAwareDropdown()
        .criteria(
          [
            { id: "none", name: "Unassigned" },
            { id: "me", name: "Me" },
            { id: "resource-owner", name: "Resource Owner" },
            { id: "account-owner", name: "Account Owner" },
            { id: "resource-tag", name: "Resource Tag" },
            { id: "account-tag", name: "Account Tag" },
            { id: "account-variable", name: "Account Variable" },
          ],
          "Select Assignee",
        )
        // Tag-based paths
        .path("resource-tag", (path) =>
          path.column(
            column
              .search("Select Tag Key", tagKeyItems)
              .withPlaceholder("Search or enter value")
              .allowCustomValue(),
          ),
        )
        .path("account-tag", (path) =>
          path
            .column(
              column
                .search("Select Tag Key", accountTagKeyItems)
                .withPlaceholder("Search or enter tag key")
                .allowCustomValue(),
            )
            .column(
              column
                .search("Select Tag Value", accountTagValueItems)
                .withPlaceholder("Search or enter tag value")
                .allowCustomValue(),
            ),
        )
        .path("account-variable", (path) =>
          path.column(column.enterValue("Enter value", "Enter value")),
        )
        // Empty paths for other options
        .path("none", (path) => path)
        .path("me", (path) => path)
        .path("resource-owner", (path) => path)
        .path("account-owner", (path) => path)
        .build(),
    [tagKeyItems, accountTagKeyItems, accountTagValueItems],
  );

  const handleSearch = useDebouncedCallback(
    (searchTerm: string, columnIndex: number, selections?: string[]) => {
      const value = searchTerm.trim();
      const activeCriteria = selections?.[0];

      startTransition(() => {
        // For resource-tag, search tag keys when in column 1
        if (activeCriteria === "resource-tag" && columnIndex === 1) {
          const searchFilter = value
            ? { single: { name: "key", value } }
            : null;
          refetchTagKeys({
            filter: searchFilter,
          });
        } else if (activeCriteria === "account-tag") {
          // columnIndex: 0=criteria, 1=tag key, 2=tag value
          if (columnIndex === 1) {
            const searchFilter = value
              ? { single: { name: "key", value } }
              : null;
            refetchAccountTagKeys({ filter: searchFilter });
          } else if (columnIndex === 2) {
            const searchFilter = value
              ? { single: { name: "value", value } }
              : null;
            refetchAccountTagValues({ filter: searchFilter });
          }
        }
      });
    },
    300,
  );

  const handleScrollEnd = useCallback(
    (columnIndex: number, selections?: string[]) => {
      const activeCriteria = selections?.[0];

      if (
        activeCriteria === "resource-tag" &&
        columnIndex === 1 &&
        hasNextTagKeys
      ) {
        loadNextTagKeys(100);
      } else if (activeCriteria === "account-tag") {
        if (columnIndex === 1 && hasNextAccountTagKeys) {
          loadNextAccountTagKeys(100);
        } else if (columnIndex === 2 && hasNextAccountTagValues) {
          loadNextAccountTagValues(100);
        }
      }
    },
    [
      hasNextTagKeys,
      hasNextAccountTagKeys,
      hasNextAccountTagValues,
      loadNextTagKeys,
      loadNextAccountTagKeys,
      loadNextAccountTagValues,
    ],
  );

  const handleChange = useCallback(
    (selections: string[]) => {
      const structuredValue = convertAssigneeSelections(
        selections,
        data.whoami?.email,
      );
      onChange?.(structuredValue);
    },
    [data.whoami?.email, onChange],
  );

  const initialSelections = useMemo(
    () => convertAssigneeValueToSelections(selectedAssignee),
    [selectedAssignee],
  );

  // Generate display text from selected assignee value
  const displayText = useMemo(
    () =>
      convertAssigneeValueToDisplayString(selectedAssignee, data.whoami?.email),
    [selectedAssignee, data.whoami?.email],
  );

  const trigger = ({ isOpen }: { isOpen: boolean }) => (
    <V2SelectField className="w-full" isOpen={isOpen} value={displayText} />
  );

  return (
    <SteppedMultiColumnDropdown
      config={config}
      initialSelections={initialSelections}
      onChange={handleChange}
      onScrollEnd={handleScrollEnd}
      onSearch={handleSearch}
      trigger={trigger}
    />
  );
}
